import { GetMessageHash } from "./getMessageHash.js";
import { validate } from "./validate.js";

/**
 * Factory: Verify a SIWE message signature
 * @param {Object} deps - Crypto dependencies
 * @param {(data: Uint8Array) => Uint8Array} deps.keccak256 - Keccak256 hash function
 * @param {(signature: {r: Uint8Array, s: Uint8Array, v: number}, hash: Uint8Array) => Uint8Array} deps.secp256k1RecoverPublicKey - secp256k1 public key recovery
 * @param {(x: bigint, y: bigint) => Uint8Array} deps.addressFromPublicKey - Address derivation from public key
 * @returns {(message: import('./SiweMessageType.js').SiweMessageType, signature: import('./SiweMessageType.js').Signature) => boolean} Function that verifies SIWE signatures
 */
export function Verify({
	keccak256,
	secp256k1RecoverPublicKey,
	addressFromPublicKey,
}) {
	const getMessageHash = GetMessageHash({ keccak256 });

	/**
	 * Verify a SIWE message signature
	 *
	 * @see https://voltaire.tevm.sh/primitives/siwe for SIWE documentation
	 * @since 0.0.0
	 * @param {import('./SiweMessageType.js').SiweMessageType} message - The SIWE message that was signed
	 * @param {import('./SiweMessageType.js').Signature} signature - The signature to verify (65 bytes: r, s, v)
	 * @returns {boolean} true if signature is valid and matches message address
	 * @throws {never}
	 * @example
	 * ```javascript
	 * import * as Siwe from './primitives/Siwe/index.js';
	 * const valid = Siwe.verify(message, signature);
	 * if (valid) {
	 *   // Signature is valid, user is authenticated
	 * }
	 * ```
	 */
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: SIWE verification requires many conditions
	return function verify(message, signature) {
		// Validate message structure
		const validationResult = validate(message);
		if (!validationResult.valid) {
			return false;
		}

		// Get message hash
		const messageHash = getMessageHash(message);

		// Signature is 65 bytes: r (32) + s (32) + v (1)
		if (signature.length !== 65) {
			return false;
		}

		const r = signature.slice(0, 32);
		const s = signature.slice(32, 64);
		const v = signature[64];

		if (v === undefined) {
			return false;
		}

		// Normalize v to recovery id (0 or 1)
		let recoveryId;
		if (v >= 27) {
			recoveryId = v - 27;
		} else {
			recoveryId = v;
		}

		if (recoveryId !== 0 && recoveryId !== 1) {
			return false;
		}

		try {
			// Recover public key from signature
			const publicKey = secp256k1RecoverPublicKey(
				{ r, s, v: recoveryId },
				/** @type {import('../Hash/HashType.js').HashType} */ (messageHash),
			);

			// Derive address from public key
			let x = 0n;
			let y = 0n;
			for (let i = 0; i < 32; i++) {
				const xByte = publicKey[i];
				const yByte = publicKey[32 + i];
				if (xByte !== undefined && yByte !== undefined) {
					x = (x << 8n) | BigInt(xByte);
					y = (y << 8n) | BigInt(yByte);
				}
			}
			const recoveredAddress = addressFromPublicKey(x, y);

			// Compare with message address
			if (recoveredAddress.length !== message.address.length) {
				return false;
			}
			for (let i = 0; i < recoveredAddress.length; i++) {
				if (recoveredAddress[i] !== message.address[i]) {
					return false;
				}
			}

			return true;
		} catch {
			return false;
		}
	};
}
